import { useState } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { useToast, humanizeError } from '../shared/Toast'

const PRIORITIES = [
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'ongoing', label: 'Ongoing' },
]

let seq = 0
const nid = () => `g${++seq}`

/**
 * Paste an existing care plan, let Ginie structure it, review, then write.
 *
 * Nothing is saved until the family confirms — the review step is the whole
 * point, since an imported plan is only as good as the document it came from.
 */
export default function CarePlanImport({ recipientId, plan, onCancel, onDone }) {
  const { user } = useAuth()
  const toast = useToast()

  const [step, setStep] = useState('paste') // paste | review
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [goals, setGoals] = useState([])
  const [meta, setMeta] = useState({})

  async function runImport() {
    if (!text.trim()) return toast.error('Paste the care plan first.')
    setBusy(true)
    try {
      const { data, error } = await supabase.functions.invoke('ginie-chat', {
        body: { mode: 'import_care_plan', message: text, care_recipient_id: recipientId },
      })
      if (error) throw error
      if (data?.error) throw new Error(data.error)

      const found = (data?.goals || []).map((g) => ({
        _id: nid(),
        title: g.title || '',
        priority: g.priority || 'ongoing',
        description: g.description || '',
        actions: (g.actions || []).map((a) => ({ _id: nid(), title: a.title || '', notes: a.notes || '' })),
      }))
      if (!found.length) {
        toast.error('Ginie could not find any goals in that text.')
        return
      }
      setGoals(found)
      setMeta({
        conditions_summary: data?.conditions_summary || '',
        routine_notes: data?.routine_notes || '',
        what_to_watch: data?.what_to_watch || '',
      })
      setStep('review')
    } catch (err) {
      toast.error(humanizeError(err))
    } finally {
      setBusy(false)
    }
  }

  const upd = (id, patch) => setGoals((l) => l.map((g) => (g._id === id ? { ...g, ...patch } : g)))
  const updAction = (gid, aid, patch) =>
    setGoals((l) => l.map((g) => (g._id === gid
      ? { ...g, actions: g.actions.map((a) => (a._id === aid ? { ...a, ...patch } : a)) }
      : g)))

  const ready = goals.filter((g) => g.title.trim())
  const actionCount = ready.reduce((n, g) => n + g.actions.filter((a) => a.title.trim()).length, 0)

  async function save() {
    if (!ready.length) return toast.error('Keep at least one goal.')
    setBusy(true)
    try {
      // Goals live as bullet lines in current_priorities, in the format the
      // existing parser reads: "• [priority] Title — description".
      const bullets = ready
        .map((g) => `• [${g.priority}] ${g.title.trim()}${g.description.trim() ? ` — ${g.description.trim()}` : ''}`)
        .join('\n')

      const notes = [
        meta.conditions_summary && `Conditions: ${meta.conditions_summary}`,
        meta.routine_notes && `Routine: ${meta.routine_notes}`,
      ].filter(Boolean).join('\n\n')

      let planId = plan?.id
      if (planId) {
        const { error } = await supabase.from('care_plans').update({
          current_priorities: bullets,
          whats_not_working: meta.what_to_watch || plan?.whats_not_working || null,
          whats_working: notes || plan?.whats_working || null,
        }).eq('id', planId)
        if (error) throw error
      } else {
        const { data, error } = await supabase.from('care_plans').insert({
          care_recipient_id: recipientId,
          created_by: user.id,
          current_priorities: bullets,
          whats_not_working: meta.what_to_watch || null,
          whats_working: notes || null,
          is_active: true,
        }).select('id').single()
        if (error) throw error
        planId = data.id
      }

      const rows = ready.flatMap((g, gi) =>
        g.actions.filter((a) => a.title.trim()).map((a, ai) => ({
          care_recipient_id: recipientId,
          care_plan_id: planId,
          goal_reference: g.title.trim(),
          title: a.title.trim(),
          description: a.notes.trim() || null,
          sort_order: gi * 100 + ai,
          created_by: user.id,
        }))
      )
      if (rows.length) {
        const { error } = await supabase.from('care_plan_actions').insert(rows)
        if (error) throw error
      }

      toast.success('Care plan imported. Review and edit in Goals → Care Plan tab.')
      onDone?.()
    } catch (err) {
      toast.error(humanizeError(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      onClick={(e) => e.target === e.currentTarget && !busy && onCancel()}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
    >
      <div style={{ background: 'var(--bg)', width: '100%', maxWidth: 'var(--maxw)', borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: 20, maxHeight: '92dvh', overflowY: 'auto' }}>
        <div className="spread" style={{ marginBottom: 12 }}>
          <h2 className="title" style={{ fontSize: 20 }}>
            {step === 'paste' ? 'Import care plan' : 'Ginie found these goals'}
          </h2>
          <button onClick={onCancel} aria-label="Close" style={{ background: 'none', border: 'none', fontSize: 22, color: 'var(--muted)' }}>✕</button>
        </div>

        {step === 'paste' ? (
          <div className="stack">
            <p className="muted" style={{ fontSize: 14, margin: 0, lineHeight: 1.6 }}>
              Paste the existing care plan below — Ginie will read it and structure
              it into goals and actions automatically. You&rsquo;ll review everything
              before it&rsquo;s saved.
            </p>
            <textarea
              className="textarea"
              style={{ minHeight: 220 }}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={'Paste care plan text here...\n\ne.g. Client goals, daily routine, health conditions, caregiver instructions, what to watch for...'}
              aria-label="Existing care plan text"
            />
            <button className="btn btn-block btn-lg" onClick={runImport} disabled={busy || !text.trim()}>
              {busy ? <span className="spinner" /> : 'Import with Ginie →'}
            </button>
          </div>
        ) : (
          <div className="stack">
            <p className="muted" style={{ fontSize: 14, margin: 0, lineHeight: 1.55 }}>
              Check each goal and action before importing — edit anything that
              isn&rsquo;t right.
            </p>

            {goals.map((g) => (
              <div key={g._id} className="card stack" style={{ gap: 8 }}>
                <div className="spread">
                  <div className="row wrap" style={{ gap: 6 }}>
                    {PRIORITIES.map((p) => (
                      <button
                        key={p.value} type="button"
                        className={`chip${g.priority === p.value ? ' chip-on' : ''}`}
                        onClick={() => upd(g._id, { priority: p.value })}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => setGoals((l) => l.filter((x) => x._id !== g._id))}
                    aria-label={`Remove ${g.title}`}
                    style={{ background: 'none', border: 'none', color: 'var(--danger)', fontSize: 18, cursor: 'pointer' }}
                  >
                    ✕
                  </button>
                </div>
                <input className="input" value={g.title} onChange={(e) => upd(g._id, { title: e.target.value })} placeholder="Goal title" aria-label="Goal title" />
                <input className="input" value={g.description} onChange={(e) => upd(g._id, { description: e.target.value })} placeholder="Description (optional)" aria-label="Goal description" />

                {g.actions.length > 0 && (
                  <div className="stack" style={{ gap: 6 }}>
                    <span className="muted" style={{ fontSize: 12 }}>Actions</span>
                    {g.actions.map((a) => (
                      <div key={a._id} className="row" style={{ gap: 6 }}>
                        <input
                          className="input grow" value={a.title}
                          onChange={(e) => updAction(g._id, a._id, { title: e.target.value })}
                          aria-label="Action"
                        />
                        <button
                          onClick={() => upd(g._id, { actions: g.actions.filter((x) => x._id !== a._id) })}
                          aria-label="Remove action"
                          style={{ background: 'none', border: 'none', color: 'var(--danger)', fontSize: 16, flexShrink: 0, cursor: 'pointer' }}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

            <div className="row" style={{ gap: 10 }}>
              <button className="btn btn-ghost" onClick={() => setStep('paste')} disabled={busy}>Back</button>
              <button className="btn btn-lg grow" onClick={save} disabled={busy || !ready.length}>
                {busy ? <span className="spinner" /> : `Import ${ready.length} goal${ready.length === 1 ? '' : 's'} and ${actionCount} action${actionCount === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>
        )}
        <div style={{ height: 8 }} />
      </div>
    </div>
  )
}
